/**
 * RoofMapper Context
 * Central state management for the RoofMapper component
 */

import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import type {
  RoofMapperConfig,
  RoofMapperFeatures,
  RoofMapperTheme,
  LocationData,
  SolarData,
  MapLayers,
  defaultTheme,
  defaultFeatures,
} from '../types';

interface RoofMapperContextType {
  // Configuration
  config: RoofMapperConfig;
  theme: RoofMapperTheme;
  features: RoofMapperFeatures;

  // Location State
  location: LocationData | null;
  setLocation: (location: LocationData | null) => void;

  // Map State
  mapMode: '2D' | '3D';
  toggleMapMode: () => void;
  setMapMode: (mode: '2D' | '3D') => void;

  // Solar Data State
  solarData: SolarData | null;
  setSolarData: (data: SolarData | null) => void;
  isLoadingSolar: boolean;
  setIsLoadingSolar: (loading: boolean) => void;

  // Layer State
  layers: MapLayers;
  toggleLayer: (layer: keyof MapLayers) => void;

  // Selected Segment
  selectedSegment: number | null;
  setSelectedSegment: (index: number | null) => void;

  // Error State
  error: string | null;
  setError: (error: string | null) => void;

  // Callbacks
  onLocationSelect?: (location: LocationData) => void;
  onSolarDataFetched?: (data: SolarData) => void;
  onError?: (error: Error) => void;
}

const RoofMapperContext = createContext<RoofMapperContextType | undefined>(undefined);

interface RoofMapperProviderInternalProps {
  children: ReactNode;
  config: RoofMapperConfig;
  theme: RoofMapperTheme;
  features: RoofMapperFeatures;
  onLocationSelect?: (location: LocationData) => void;
  onSolarDataFetched?: (data: SolarData) => void;
  onError?: (error: Error) => void;
}

export function RoofMapperProviderInternal({
  children,
  config,
  theme,
  features,
  onLocationSelect,
  onSolarDataFetched,
  onError,
}: RoofMapperProviderInternalProps) {
  // Location State
  const [location, setLocationState] = useState<LocationData | null>(null);

  // Map State
  const [mapMode, setMapMode] = useState<'2D' | '3D'>(config.defaultMapMode || '2D');

  // Solar Data State
  const [solarData, setSolarData] = useState<SolarData | null>(null);
  const [isLoadingSolar, setIsLoadingSolar] = useState(false);

  // Layer State
  const [layers, setLayers] = useState<MapLayers>({
    rgb: false,
    flux: false,
    dsm: false,
  });

  // Selected Segment
  const [selectedSegment, setSelectedSegment] = useState<number | null>(null);

  // Error State
  const [error, setError] = useState<string | null>(null);

  // Actions
  const setLocation = useCallback(
    (newLocation: LocationData | null) => {
      setLocationState(newLocation);
      if (newLocation && onLocationSelect) {
        onLocationSelect(newLocation);
      }
    },
    [onLocationSelect]
  );

  const toggleMapMode = useCallback(() => {
    setMapMode((prev) => (prev === '2D' ? '3D' : '2D'));
  }, []);

  const toggleLayer = useCallback((layer: keyof MapLayers) => {
    setLayers((prev) => ({
      ...prev,
      [layer]: !prev[layer],
    }));
  }, []);

  const value: RoofMapperContextType = {
    config,
    theme,
    features,
    location,
    setLocation,
    mapMode,
    toggleMapMode,
    setMapMode,
    solarData,
    setSolarData,
    isLoadingSolar,
    setIsLoadingSolar,
    layers,
    toggleLayer,
    selectedSegment,
    setSelectedSegment,
    error,
    setError,
    onLocationSelect,
    onSolarDataFetched,
    onError,
  };

  return (
    <RoofMapperContext.Provider value={value}>
      {children}
    </RoofMapperContext.Provider>
  );
}

// Custom hook to use RoofMapper context
export function useRoofMapperContext(): RoofMapperContextType {
  const context = useContext(RoofMapperContext);
  if (context === undefined) {
    throw new Error('useRoofMapperContext must be used within RoofMapperProvider');
  }
  return context;
}

export default RoofMapperContext;
